import CardAIStream from "@/components/card-ai-stream";
import CardCompanyActivity, {
  CardCompanyActivitySkeleton,
} from "@/components/company/card-company-activity";
import { CardLoadingWrapper } from "@/components/company/company-animations";
import CompanySectionCard, {
  CompanyCardSkeleton,
} from "@/components/company/company-section-card";

import CardCompanyEmployeesSuggested from "@/components/company/card-company-employees-suggested";
import CardCompanyHeadcountHistory from "@/components/company/card-company-headcount-history";
import CardCompanyFinancials, {
  CardCompanyFinancialsSkeleton,
} from "@/components/company/card-company-financials";
import CardCompanyMarket from "@/components/company/card-company-market";
import CardCompanySummary from "@/components/company/card-company-summary";
import CardCompanyTechnologies from "@/components/company/card-company-technologies";
import CardCompanyWebTraffic from "@/components/company/card-company-web-traffic";
import CardOfferSummary from "@/components/company/card-offer-summary";
import CardQuickActions from "@/components/company/card-quick-actions";
import { Card } from "@/components/ui/card";
import { useAreSectionsPending } from "@/contexts/company-sections-context";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useCompanyPageWebProfile } from "@/hooks/useCompanyPageWebProfile";
import { useTranslation } from "@/hooks/useTranslation";
import { COMPANY_CARD_SECTIONS } from "@/lib/company-card-sections";
import { hasHeadcountHistoryData } from "@/lib/company-events";
import {
  hasActivityData,
  hasKeyFiguresData,
  hasLocationData,
  hasMarketData,
  hasSummaryData,
  hasTechnologiesData,
  hasWebTrafficData,
} from "@/lib/company-data-availability";
import { cn } from "@/lib/utils";
import {
  hasWebProfileNotice,
  settledWebKeyData,
  webHighlightsOf,
  webSynthesisOf,
} from "@/stores/company-web-preview-store";
import {
  formatWorkingHoursDay,
  hasKnownWorkingHours,
  isOpenAt,
  WORKING_HOURS_DAYS,
} from "@/lib/company-working-hours";
import { getGoogleMapsEmbedQuery } from "@/utils/google-maps";
import { CompanyWorkingHours, DayOfWeek } from "@shaarp/common";
import { ClockIcon, MapPinIcon } from "lucide-react";
import { useCompanyTabContext } from "./company-tab-context";

const WEEK_INDEX_TO_DAY: readonly DayOfWeek[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

/** The week, from today's opening hours (and yesterday's past midnight). */
function WorkingHoursSchedule({
  workingHours,
}: {
  workingHours: CompanyWorkingHours;
}) {
  const { t } = useTranslation();
  const now = new Date();
  const today = WEEK_INDEX_TO_DAY[now.getDay()];
  const yesterday = WEEK_INDEX_TO_DAY[(now.getDay() + 6) % 7];
  const isOpenNow = isOpenAt(
    workingHours[today] ?? [],
    now.getHours() * 60 + now.getMinutes(),
    workingHours[yesterday] ?? [],
  );

  return (
    <div className="flex flex-col gap-2 border-l px-6 py-4 shrink-0">
      <div className="flex items-center gap-2 mb-2">
        <ClockIcon className="w-5 h-5 text-muted-foreground" />
        <span className="text-base font-semibold">
          {t("company.signals.workingHours.title")}
        </span>
      </div>
      {WORKING_HOURS_DAYS.map((day) => (
        <div
          key={day}
          className={cn(
            "flex items-center gap-6 rounded-lg px-4 py-2",
            day === today && isOpenNow
              ? "bg-green-100 dark:bg-green-900/30"
              : "bg-muted/30",
          )}
        >
          <span className="w-24 text-sm font-semibold">
            {t(`company.signals.workingHours.days.${day}`)}
          </span>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatWorkingHoursDay(workingHours[day], t)}
          </span>
        </div>
      ))}
    </div>
  );
}

function CompanyLocationCard({
  mapsQuery,
  workingHours,
}: {
  mapsQuery: string;
  workingHours: CompanyWorkingHours;
}) {
  const { t } = useTranslation();

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-muted">
          <MapPinIcon className="w-5 h-5" />
        </div>
        <span className="text-lg font-semibold">
          {t("company.insights.location")}
        </span>
      </div>
      <div className="flex overflow-hidden">
        <iframe
          src={`https://www.google.com/maps/embed/v1/place?key=${
            import.meta.env.VITE_GOOGLE_MAPS_API_KEY
          }&q=${mapsQuery}`}
          className="block flex-1 min-h-[400px] rounded-bl-xl"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        {hasKnownWorkingHours(workingHours) && (
          <WorkingHoursSchedule workingHours={workingHours} />
        )}
      </div>
    </Card>
  );
}

export default function CompanyInsightsPage() {
  const { company, done } = useCompanyTabContext();
  const arePeopleVisible = useArePeopleVisible();
  const isActivityRefreshing = useAreSectionsPending(
    COMPANY_CARD_SECTIONS.activity,
  );
  const isKeyFiguresRefreshing = useAreSectionsPending(
    COMPANY_CARD_SECTIONS.keyFigures,
  );
  const isSummaryRefreshing = useAreSectionsPending(
    COMPANY_CARD_SECTIONS.summary,
  );
  const isOfferSummaryRefreshing = useAreSectionsPending(
    COMPANY_CARD_SECTIONS.offerSummary,
  );

  // The web's figures fill the tiles the base leaves empty, never the base.
  const { state: webProfile, retry: retryWebProfile } =
    useCompanyPageWebProfile(company);
  const webKeyData = settledWebKeyData(webProfile);
  const webFigures = {
    revenue: webKeyData?.revenue,
    headcount: webKeyData?.headcount,
  };

  const { t } = useTranslation();
  const mapsQuery = getGoogleMapsEmbedQuery(company);

  return (
    <div className="flex flex-col gap-4 @container sm:gap-5">
      {/* The snapshot a seller reads first; the detail lives in the other tabs. */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 items-start @[1050px]:grid-cols-[2fr_1fr] stagger-fade-in">
        {/* Left column: contents flattens children in single-col for ordering */}
        <div className="contents @[1050px]:flex @[1050px]:flex-col @[1050px]:gap-5">
          <div className="order-1 @[1050px]:order-none empty:hidden">
            <CompanySectionCard
              sections={COMPANY_CARD_SECTIONS.activity}
              hasData={
                hasActivityData(company) ||
                !!webSynthesisOf(webProfile).synthesis ||
                webHighlightsOf(webProfile).length > 0 ||
                hasWebProfileNotice(webProfile)
              }
              skeleton={<CardCompanyActivitySkeleton />}
            >
              <CardLoadingWrapper isLoading={isActivityRefreshing}>
                <CardCompanyActivity
                  company={company}
                  webProfile={webProfile}
                  onWebRetry={retryWebProfile}
                />
              </CardLoadingWrapper>
            </CompanySectionCard>
          </div>
          <div className="order-2 @[1050px]:order-none empty:hidden">
            <CompanySectionCard
              sections={COMPANY_CARD_SECTIONS.keyFigures}
              hasData={
                hasKeyFiguresData(company) ||
                !!webFigures.revenue ||
                !!webFigures.headcount
              }
              skeleton={<CardCompanyFinancialsSkeleton />}
            >
              <CardCompanyFinancials
                company={company}
                isRefreshing={isKeyFiguresRefreshing}
                hideGrowthSignals
                webFigures={webFigures}
              />
            </CompanySectionCard>
          </div>
          <div className="order-4 @[1050px]:order-none empty:hidden">
            <CardCompanyEmployeesSuggested company={company} />
          </div>
          <div className="order-6 @[1050px]:order-none empty:hidden">
            {/* The advice reads the whole sheet server-side: it waits for the run. */}
            <CardAIStream
              title={t("company.insights.aiAdvice")}
              streamUrl={`/company/${company.id}/advice`}
              launchFetch={done}
              showAvatar={false}
            />
          </div>
        </div>

        {/* Right column */}
        <div className="contents @[1050px]:flex @[1050px]:flex-col @[1050px]:gap-5 @[1050px]:min-w-0">
          <div className="order-3 @[1050px]:order-none empty:hidden">
            <CompanySectionCard
              sections={COMPANY_CARD_SECTIONS.summary}
              hasData={hasSummaryData(company) || !!webKeyData?.foundedYear}
              skeleton={<CompanyCardSkeleton />}
            >
              <CardLoadingWrapper isLoading={isSummaryRefreshing}>
                <CardCompanySummary
                  company={company}
                  webFoundedYear={webKeyData?.foundedYear}
                />
              </CardLoadingWrapper>
            </CompanySectionCard>
          </div>
          <div className="order-5 @[1050px]:order-none empty:hidden">
            <CardQuickActions />
          </div>
          <div className="order-7 @[1050px]:order-none empty:hidden">
            <CompanySectionCard
              sections={COMPANY_CARD_SECTIONS.market}
              hasData={hasMarketData(company)}
              skeleton={<CompanyCardSkeleton />}
            >
              <CardCompanyMarket company={company} />
            </CompanySectionCard>
          </div>
          <div className="order-8 @[1050px]:order-none empty:hidden">
            <CompanySectionCard
              sections={COMPANY_CARD_SECTIONS.webTraffic}
              hasData={hasWebTrafficData(company)}
              skeleton={<CompanyCardSkeleton />}
            >
              <CardCompanyWebTraffic company={company} />
            </CompanySectionCard>
          </div>
          <div className="order-9 @[1050px]:order-none empty:hidden">
            <CompanySectionCard
              sections={COMPANY_CARD_SECTIONS.technologies}
              hasData={hasTechnologiesData(company)}
              skeleton={<CompanyCardSkeleton />}
            >
              <CardCompanyTechnologies company={company} />
            </CompanySectionCard>
          </div>
        </div>

        {/* Offer summary – always full-width */}
        <div className="order-10 @[1050px]:order-none @[1050px]:col-span-2 empty:hidden">
          <CompanySectionCard
            sections={COMPANY_CARD_SECTIONS.offerSummary}
            hasData={!!company.offerSummary}
            skeleton={<CompanyCardSkeleton />}
          >
            <CardOfferSummary
              offerSummary={company.offerSummary ?? ""}
              isLoading={isOfferSummaryRefreshing}
            />
          </CompanySectionCard>
        </div>

        {/* Full-width cards */}
        <div className="order-11 @[1050px]:order-none @[1050px]:col-span-2 empty:hidden">
          <CompanySectionCard
            sections={COMPANY_CARD_SECTIONS.location}
            hasData={hasLocationData(company)}
            skeleton={<CompanyCardSkeleton className="min-h-[200px]" />}
          >
            {mapsQuery && (
              <CompanyLocationCard
                mapsQuery={mapsQuery}
                workingHours={company.workingHours}
              />
            )}
          </CompanySectionCard>
        </div>

        {/* The workforce curve lives beside the people; a team that sees no
            people still reads it here, as on mobile. */}
        {!arePeopleVisible && (
          <div className="order-12 @[1050px]:order-none @[1050px]:col-span-2 empty:hidden">
            <CompanySectionCard
              sections={COMPANY_CARD_SECTIONS.headcountHistory}
              hasData={hasHeadcountHistoryData(company)}
              skeleton={<CompanyCardSkeleton />}
            >
              <CardCompanyHeadcountHistory company={company} />
            </CompanySectionCard>
          </div>
        )}
      </div>
    </div>
  );
}
